/**
 * Shared year/month accordion for the Inspiration archive.
 *
 * Props:
 *  - mode: "admin" | "read"
 *  - basePath: "/admin/ideas" or "/portal/ideas"
 *  - reloadKey: bump to force a refresh from server
 *  - extraToolbar: optional ReactNode rendered above the archive
 *
 * Default expansion: current year + most recent month within it.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CaretDown,
  CaretRight,
  CircleDashed,
  CheckCircle,
  ArrowRight,
  FileText,
  DotsSixVertical,
} from "@phosphor-icons/react";
import { inspirationsApi, inspirationsAdminApi } from "../../api/inspirations";
import { monthLabel, now } from "../../lib/dates";
import SortableList from "../courses/SortableList";

function ArticleRow({ article, basePath, isAdmin, dragHandle, navigate, t }) {
  return (
    <div
      data-testid={`inspiration-article-${article.id}`}
      className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-slate-900 transition-colors"
    >
      {isAdmin && (
        <button
          type="button"
          ref={dragHandle?.setActivatorNodeRef}
          {...(dragHandle?.attributes || {})}
          {...(dragHandle?.listeners || {})}
          data-testid={`inspiration-article-drag-${article.id}`}
          aria-label="Réorganiser"
          className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-slate-400 hover:text-slate-700 touch-none"
        >
          <DotsSixVertical size={16} weight="bold" />
        </button>
      )}
      <button
        type="button"
        onClick={() => navigate(`${basePath}/${article.id}`)}
        data-testid={`inspiration-article-open-${article.id}`}
        className="flex-1 flex items-center gap-3 text-left"
      >
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
          <FileText size={16} weight="regular" />
        </span>
        <span className="flex-1 text-sm font-medium text-slate-900">
          {article.title}
        </span>
        {isAdmin && article.status === "draft" && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
            <CircleDashed size={12} weight="bold" />
            {t("inspiration.status_draft")}
          </span>
        )}
        {isAdmin && article.status === "published" && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
            <CheckCircle size={12} weight="fill" />
            {t("inspiration.status_published")}
          </span>
        )}
        <ArrowRight
          size={14}
          weight="bold"
          className="text-slate-400 group-hover:text-slate-900 group-hover:translate-x-1 transition-all"
        />
      </button>
    </div>
  );
}

export default function InspirationArchive({
  mode = "read",
  basePath = "/portal/ideas",
  reloadKey = 0,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = mode === "admin";
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);
  // expanded state: {year: bool, "year-month": bool}
  const [expanded, setExpanded] = useState({});
  // articles cache: {"year-month": [articles]}
  const [articles, setArticles] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await inspirationsApi.archive();
      setArchive(data);
      if (data.length > 0) {
        const current = now();
        const currentYear = data.find((y) => y.year === current.year) || data[0];
        const initial = { [currentYear.year]: true };
        const mostRecentMonth = currentYear.months[0];
        if (mostRecentMonth) {
          initial[`${currentYear.year}-${mostRecentMonth.month}`] = true;
        }
        setExpanded((prev) => ({ ...initial, ...prev }));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const toggleYear = (year) =>
    setExpanded((e) => ({ ...e, [year]: !e[year] }));

  const toggleMonth = useCallback(
    async (year, month) => {
      const key = `${year}-${month}`;
      const isOpen = !!expanded[key];
      setExpanded((e) => ({ ...e, [key]: !isOpen }));
      if (!isOpen && !articles[key]) {
        const rows = await inspirationsApi.listForPeriod(year, month);
        setArticles((a) => ({ ...a, [key]: rows }));
      }
    },
    [expanded, articles],
  );

  // Pre-load articles for the months that start expanded
  useEffect(() => {
    Object.keys(expanded).forEach(async (k) => {
      if (!expanded[k] || articles[k]) return;
      const parts = k.split("-");
      if (parts.length !== 2) return;
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      if (!year || !month) return;
      const rows = await inspirationsApi.listForPeriod(year, month);
      setArticles((a) => ({ ...a, [k]: rows }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const empty = !loading && archive.length === 0;

  return (
    <div className="space-y-3">
      {loading && (
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      )}

      {empty && (
        <p
          data-testid="inspiration-empty"
          className="text-sm text-slate-500 py-12 text-center"
        >
          {isAdmin
            ? t("inspiration.empty_admin")
            : t("inspiration.empty_read")}
        </p>
      )}

      {archive.map((y) => (
        <div
          key={y.year}
          data-testid={`inspiration-year-${y.year}`}
          className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
        >
          <button
            type="button"
            onClick={() => toggleYear(y.year)}
            data-testid={`inspiration-year-toggle-${y.year}`}
            className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
          >
            <span className="text-slate-400">
              {expanded[y.year] ? (
                <CaretDown size={16} weight="bold" />
              ) : (
                <CaretRight size={16} weight="bold" />
              )}
            </span>
            <span className="font-heading text-base md:text-lg font-bold tracking-tight text-slate-900">
              {y.year}
            </span>
            <span className="ml-auto text-xs text-slate-500">
              {y.months.reduce((sum, m) => sum + m.count, 0)}{" "}
              {t("inspiration.articles")}
            </span>
          </button>

          {expanded[y.year] && (
            <div className="border-t border-slate-100">
              {y.months.map((m) => {
                const key = `${y.year}-${m.month}`;
                const isMonthOpen = !!expanded[key];
                const monthArticles = articles[key] || [];
                return (
                  <div key={m.month} className="border-b border-slate-100 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => toggleMonth(y.year, m.month)}
                      data-testid={`inspiration-month-toggle-${y.year}-${m.month}`}
                      className="w-full flex items-center gap-3 pl-9 pr-5 py-3 text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="text-slate-400">
                        {isMonthOpen ? (
                          <CaretDown size={14} weight="bold" />
                        ) : (
                          <CaretRight size={14} weight="bold" />
                        )}
                      </span>
                      <span className="text-sm font-medium text-slate-900 capitalize">
                        {monthLabel(m.month, t)}
                      </span>
                      <span className="ml-auto text-xs text-slate-500">
                        {m.count} {t("inspiration.articles")}
                      </span>
                    </button>

                    {isMonthOpen && (
                      <div className="pl-14 pr-5 pt-2 pb-5 space-y-2">
                        {monthArticles.length === 0 ? (
                          <p className="text-xs text-slate-400 py-2">
                            {t("inspiration.no_article_in_period")}
                          </p>
                        ) : isAdmin ? (
                          <SortableList
                            items={monthArticles}
                            onReorder={async (next) => {
                              setArticles((a) => ({ ...a, [key]: next }));
                              const items = next.map((a, idx) => ({
                                id: a.id,
                                order: idx + 1,
                              }));
                              try {
                                await inspirationsAdminApi.reorder(items);
                              } catch {
                                // Reload month on failure
                                const rows = await inspirationsApi.listForPeriod(
                                  y.year,
                                  m.month,
                                );
                                setArticles((a) => ({ ...a, [key]: rows }));
                              }
                            }}
                            renderRow={(a, dragHandle) => (
                              <ArticleRow
                                key={a.id}
                                article={a}
                                basePath={basePath}
                                isAdmin
                                dragHandle={dragHandle}
                                navigate={navigate}
                                t={t}
                              />
                            )}
                          />
                        ) : (
                          monthArticles.map((a) => (
                            <ArticleRow
                              key={a.id}
                              article={a}
                              basePath={basePath}
                              navigate={navigate}
                              t={t}
                            />
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
